import { CounterLine, DeleteObjectLine, SettingLine, UnknownLine, Z3KConfigSettingNumber, Z3KConfigSettingTypesNumber, } from '../utils/types';
import { decodeA300Interface, decodeA300Params, decodeAPN, decodeGSM, decodeLocalSubnetName, decodeLogin, decodeMqttAddress, decodePassword, decodePort, decodePort485, decodeS7, decodeServers, decodeServerSetting, decodeServiceContact, decodeStockConfig, decodeTimeZone, decodeUsbPassword, decodeUseReserveBattery, decodeWiFi, decodeWiFiAccessPoint, encodeA300Interface, encodeA300Params, encodeAPN, encodeDeviceInfo, encodeGsmSettings, encodeGsmSettingsNotification, encodeLocalSubnetName, encodeLogin, encodeMqttAddress, encodePort, encodePortRS485, encodeServers, encodeServerSettings, encodeServiceContact, encodeStockConfig, encodeTimezone, encodeUsbPassword, encodeUseReserveBattery, encodeWifiAccessPoint, encodeWifiSettings, } from './setting-specifications';
import { object_types } from 'z3k-parser/lib/auxiliary';
import { Z3KAnyObjectSpec } from './objects/object-specifications';
import { z3k_object_id_by_field } from 'webui/Z3KConfigParser/parser/utils/object-id-by-field';
import { decodeArguments, getAuthor, ObjectLine, } from 'z3k-parser/parser/utils/object-line';
export function* iterConfigLines(config) {
    yield encodeDeviceInfo(config.device);
    if (config.gsm_settings) {
        yield encodeGsmSettings(config.gsm_settings);
        if (config.gsm_settings.notification !== undefined) {
            yield encodeGsmSettingsNotification(config.gsm_settings.notification || 0);
        }
    }
    yield encodeTimezone(config.timezone);
    if (config.servers) {
        yield encodeServers(config.servers);
    }
    if (config.port) {
        yield encodePort(config.port);
    }
    if (config.password) {
        yield encodeLogin(config.login, config.password);
    }
    if (config.apn) {
        yield encodeAPN(config.apn);
    }
    if (config.usbpassword) {
        yield encodeUsbPassword(config.usbpassword);
    }
    if (config.service_contact) {
        yield encodeServiceContact(config.service_contact);
    }
    if (config.wifi_settings) {
        yield encodeWifiSettings(config.wifi_settings);
    }
    if (config.port_rs485) {
        yield encodePortRS485(config.port_rs485);
    }
    if (config.use_reserve_battery !== undefined) {
        yield encodeUseReserveBattery(config.use_reserve_battery);
    }
    if (config.a300_diag_interface) {
        yield encodeA300Interface(config.a300_diag_interface);
    }
    if (config.a300_diag_available_params) {
        yield encodeA300Params(config.a300_diag_available_params);
    }
    if (config.stockconfig) {
        yield encodeStockConfig(config.stockconfig);
    }
    if (config.server_settings) {
        yield encodeServerSettings(config.server_settings);
    }
    if (config.local_subnet_name) {
        yield encodeLocalSubnetName(config.local_subnet_name);
    }
    if (config.mqtt_address) {
        yield encodeMqttAddress(config.mqtt_address);
    }
    if (config.wifi_access_point) {
        yield encodeWifiAccessPoint(config.wifi_access_point);
    }
    for (const un of config.unknown || []) {
        yield new UnknownLine(un);
    }
    for (const field of object_types) {
        if (field in config) {
            for (const obj of config[field] || []) {
                yield Z3KAnyObjectSpec[field].toStr(z3k_object_id_by_field[field], obj);
            }
        }
    }
    for (const author in config.counters || {}) {
        yield new CounterLine(Number(author), config.counters[author]);
    }
}
export function config2Str(config) {
    return (Array.from(iterConfigLines(config))
        .map((line) => line.toString())
        .join('\r\n') + '\r\n');
}
export const LineRegExp = /^#([ZSY])(\d+)([~:=$])(.*)$/;
export function parseLine(line) {
    const match = line.match(LineRegExp);
    if (match) {
        const [, kind, number_str, action, value_str] = match;
        const number = Number(number_str);
        if (!number) {
            return new UnknownLine(line);
        }
        switch (kind) {
            case 'S':
                return new SettingLine(number, value_str);
            case 'Z':
                switch (action) {
                    case '~':
                        return new DeleteObjectLine(number);
                    case ':':
                    case '=':
                        if (value_str == '*') {
                            return new CounterLine(getAuthor(number), number & 0xfff);
                        }
                        else {
                            const [subtype, ...args] = decodeArguments(value_str);
                            return new ObjectLine(number, 
                            //action,
                            subtype, args);
                        }
                }
        }
    }
    return new UnknownLine(line);
}
export function* parseConfig(config) {
    for (const str of config.trim().split('\n')) {
        const line = str.trim();
        const parsed = parseLine(line);
        yield { line: line, parsed: parsed };
    }
}
export function str2Config(text) {
    const config = {};
    const unknown = [];
    const setting_by_id = {};
    const counters = {};
    for (const value of parseConfig(text)) {
        if (value.parsed instanceof SettingLine) {
            const setting_id = value.parsed.settingId;
            if (!Z3KConfigSettingTypesNumber.includes(setting_id)) {
                unknown.push(value.line);
            }
            setting_by_id[setting_id] = value.parsed;
        }
        else if (value.parsed instanceof ObjectLine) {
            const setting_name = z3k_object_id_by_field[value.parsed.subtype];
            const serializer = Z3KAnyObjectSpec[setting_name] || null;
            if (serializer !== null) {
                const obj = serializer.fromStr(value.parsed.objectId, value.parsed.args);
                const arr = config[setting_name];
                if (arr == null) {
                    //@ts-ignore
                    config[setting_name] = [obj];
                }
                else {
                    //@ts-ignore
                    arr.push(obj);
                }
            }
            else {
                unknown.push(value.line);
            }
        }
        else if (value.parsed instanceof CounterLine) {
            counters[value.parsed.author] = value.parsed.value;
        }
        else if (value.parsed instanceof UnknownLine) {
            unknown.push(value.parsed.content);
        }
    }
    Object.entries(config).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.sort((a, b) => {
                const ap = a?.position ?? 0, bp = b?.position ?? 0;
                if (ap === 0 && bp === 0)
                    return 0;
                if (ap === 0)
                    return 1;
                if (bp === 0)
                    return -1;
                return ap - bp;
            });
        }
    });
    return {
        ...config,
        counters: counters,
        device: decodeS7(setting_by_id[Z3KConfigSettingNumber.device]),
        gsm_settings: decodeGSM(setting_by_id[Z3KConfigSettingNumber.gsm_settings], setting_by_id[Z3KConfigSettingNumber.gsm_settings_notification]),
        timezone: decodeTimeZone(setting_by_id[Z3KConfigSettingNumber.timezone]),
        port_rs485: decodePort485(setting_by_id[Z3KConfigSettingNumber.port_rs485]),
        servers: decodeServers(setting_by_id[Z3KConfigSettingNumber.servers]),
        port: decodePort(setting_by_id[Z3KConfigSettingNumber.port]),
        login: decodeLogin(setting_by_id[Z3KConfigSettingNumber.login]),
        password: decodePassword(setting_by_id[Z3KConfigSettingNumber.login]),
        apn: decodeAPN(setting_by_id[Z3KConfigSettingNumber.apn]),
        usbpassword: decodeUsbPassword(setting_by_id[Z3KConfigSettingNumber.usb_password]),
        service_contact: decodeServiceContact(setting_by_id[Z3KConfigSettingNumber.service_contact]),
        wifi_settings: decodeWiFi(setting_by_id[Z3KConfigSettingNumber.wifi_settings]),
        use_reserve_battery: decodeUseReserveBattery(setting_by_id[Z3KConfigSettingNumber.use_reserve_battery]),
        a300_diag_interface: decodeA300Interface(setting_by_id[Z3KConfigSettingNumber.a300_diag_interface]),
        a300_diag_available_params: decodeA300Params(setting_by_id[Z3KConfigSettingNumber.a300_diag_available_params]),
        stockconfig: decodeStockConfig(setting_by_id[Z3KConfigSettingNumber.stock_config]),
        server_settings: decodeServerSetting(setting_by_id[Z3KConfigSettingNumber.server_settings]),
        local_subnet_name: decodeLocalSubnetName(setting_by_id[Z3KConfigSettingNumber.local_subnet_name]),
        mqtt_address: decodeMqttAddress(setting_by_id[Z3KConfigSettingNumber.mqtt_address]),
        wifi_access_point: decodeWiFiAccessPoint(setting_by_id[Z3KConfigSettingNumber.wifi_access_point]),
        unknown: unknown,
    };
}
